/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Button,
  InlineLoading,
  PasswordInput,
  TextInput
} from 'carbon-components-react';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMount } from 'react-use';

import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { LoaderStates } from '@ibm-watson/shared/ts';
import { useChannelHelpers } from '../../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../../hooks/useFormHelpers';
import { disableSaveCreateButton } from '../../../../store/channels/actions';
import { selectCurrentIntegrationId } from '../../../../store/channels/selectors';
import { selectContactCenterConnectionStatus } from '../../../../store/phone/selectors';
import { DEFAULT_NICE_CXONE_CONTACT_ID } from '../../constants';
import { niceInfo, subscriptionType } from '../../phone-constants';
import { FIELDS } from '../../schema';
import { ContactCenter as messages } from '../messages';
import '../ConnectToContactCenterStep.scss';

export const NiceCXoneForm = () => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { connectToContactCenter } = useChannelHelpers(formMethods);
  const {
    getFieldValue,
    isValidAndPopulated,
    registerInputField,
    updateFieldValue
  } = useFormHelpers(formMethods);

  const connectionStatus = useAppSelector(selectContactCenterConnectionStatus);
  const currentIntegrationId = useAppSelector(selectCurrentIntegrationId);

  const [statusMessage, setStatusMessage] = useState<string>('');
  const [testConnection, setTestConnection] = useState<boolean>(false);

  const fieldsValid =
    isValidAndPopulated(FIELDS.CONTACT_CENTER.ACCESS_KEY_ID) &&
    isValidAndPopulated(FIELDS.CONTACT_CENTER.ACCESS_KEY_SECRET);

  /**
   * On mount, set the default Nice contact ID into the draft
   * settings. Additionally, this form requires Tanya to click on
   * Test connection before the Save and Exit button can be
   * enabled.
   */
  useMount(() => {
    updateFieldValue(
      FIELDS.CONTACT_CENTER.NICE_CXONE_CONTACT_ID,
      DEFAULT_NICE_CXONE_CONTACT_ID
    );

    dispatch(disableSaveCreateButton(true));
  });

  /**
   * Gets the connection status of request to Nice CXOne
   * If loading, test connection button is disabled
   * Successful/failed status will show an icon next to test connection button
   */
  useEffect(() => {
    let message = null;
    switch (connectionStatus) {
      case LoaderStates.ACTIVE:
        message = t(messages.connecting);
        break;
      case LoaderStates.ERROR:
        message = t(messages.notConnected);
        break;
      case LoaderStates.SUCCESS:
        // On test connection success, enabling the save and exit button.
        dispatch(disableSaveCreateButton(false));
        message = t(messages.connected);
        break;
      default:
        message = '';
    }
    setStatusMessage(message);
  }, [connectionStatus, t, dispatch]);

  useEffect(() => {
    if (testConnection) {
      connectToContactCenter(subscriptionType.NICE_CXONE);
      setTestConnection(false);
    }
  }, [
    connectToContactCenter,
    currentIntegrationId,
    dispatch,
    getFieldValue,
    testConnection
  ]);

  const subscribeInputData = [
    {
      field: FIELDS.CONTACT_CENTER.ACCESS_KEY_ID,
      id: 'access_key_id_input',
      labelText: messages.accessKeyIdInputLabel,
      placeholder: t(messages.accessKeyIdInputPlaceHolder, {
        contactCenter: niceInfo.contactCenter
      }),
      isPassword: true
    },
    {
      field: FIELDS.CONTACT_CENTER.ACCESS_KEY_SECRET,
      id: 'access_key_secret_input',
      labelText: messages.accessKeySecretInputLabel,
      placeholder: t(messages.accessKeySecretInputPlaceHolder, {
        contactCenter: niceInfo.contactCenter
      }),
      isPassword: true
    }
  ];

  return (
    <>
      <div className="ConnectToContactCenterStep__title">
        <FormattedMessage {...messages.connectToContactCredentials} />
      </div>
      <div className="TabContent__spacer8">
        {subscribeInputData.map((inputData, index) => (
          <div
            className="TabContent__field-container ConnectToContactCenterStep__connect-input-container"
            key={`text-input-${index}`}>
            {inputData.isPassword ? (
              <PasswordInput
                {...registerInputField({
                  field: inputData.field,
                  isPasswordField: true,
                  placeholderText: inputData.placeholder,
                  readOnly: false
                })}
                id={inputData.id}
                labelText={
                  <div className="ConnectToContactCenterStep__input-label">
                    <FormattedMessage {...inputData.labelText} />
                  </div>
                }
              />
            ) : (
              <TextInput
                {...registerInputField({
                  field: inputData.field,
                  placeholderText: inputData.placeholder,
                  readOnly: false
                })}
                id={inputData.id}
                labelText={
                  <div className="ConnectToContactCenterStep__input-label">
                    <FormattedMessage {...inputData.labelText} />
                  </div>
                }
              />
            )}
          </div>
        ))}
      </div>
      <div className="ConnectToContactCenterStep__connect-button-container">
        <Button
          id="connect_id_button"
          className="ConnectToContactCenterStep__connect-button"
          kind="tertiary"
          onClick={() => {
            ctaEvents.phoneTestConnectionClicked({
              category: 'Phone - Test connection with contact center'
            });
            setTestConnection(true);
          }}
          disabled={!fieldsValid || connectionStatus === LoaderStates.ACTIVE}
          size="sm">
          <FormattedMessage {...messages.connectAccountButton} />
        </Button>
        {fieldsValid && connectionStatus !== LoaderStates.IDLE && (
          <div className="ConnectToContactCenterStep__connect-button-status">
            <InlineLoading
              description={statusMessage}
              status={connectionStatus}
            />
          </div>
        )}
      </div>
    </>
  );
};
